import {BrowserModule} from '@angular/platform-browser';
import {CUSTOM_ELEMENTS_SCHEMA, NgModule} from '@angular/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient, withInterceptorsFromDi, withFetch
} from '@angular/common/http';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration
} from '@azure/msal-angular';
import {GroupGuardService} from './services/group-guard.service';


import {EmployeeService} from './services/employee/employee.service';
import {ProjectService} from './services/project/project.service';
import {TaskService} from './services/task/task.service';
import {TimeCardService} from './services/timecard/timecard.service';
import {WeekService} from './services/week/week.service';
import {InfoMessageService} from "./services/infomessage/infomessage.service";
import {TemplateService} from "./services/template/template.service";
import {ReportingService} from "./services/reporting/reporting.service";
import {SysadminService} from './services/sysadmin/sysadmin.service';
import {InternalMsgService} from './services/internal.msg.service';
import {AuthGuard} from './guards/auth.guard';
import {SysAdminGuard} from "./guards/sysadmin.guard";
import {GroupAdminService} from './services/groupadmin/groupadmin.service';

import {CreateTimecardComponent} from './createtimecard/createtimecard.component';
import {HomeComponent} from './home/home.component';
import {RecentTimecardsComponent} from './recenttimecard/recenttimecards.component';
import {SysadminComponent} from './sysadmin/sysadmin.component';
import {DetailedReportingComponent} from './detailedreporting/detailedreporting.component';
import {ReportsComponent} from './reports/reports.component';
import {SysadminBatchCheckComponent} from './sysadmin/sysadmin-batch-check/sysadmin-batch-check.component';
import {SysadminSetInfomsgComponent} from './sysadmin/sysadmin-set-infomsg/sysadmin-set-infomsg.component';
import {GroupPeriodReportComponent} from './groupperiodreport/groupperiodreport.component';
import {GroupMonthReportComponent} from './groupmonthreport/groupmonthreport.component';
import {GroupWeekReportComponent} from './groupweekreport/groupweekreport.component';
import {GroupProjectReportComponent} from './groupprojectreport/groupprojectreport.component';
import {GroupadminComponent} from './groupadmin/groupadmin.component';
import {SysadminSetAdminComponent} from './sysadmin/sysadmin-set-admin/sysadmin-set-admin.component';

import {ErrorComponent} from './error/error.component';
import {SystemUnavailComponent} from './systemunavail/systemunavail.component';
import {PendingChangesGuard} from './services/pendingchanges.guard';
import {AccessDeniedComponent} from './access-denied/access-denied.component';
import {SystemUnavailService} from './services/systemunavail/systemunavail.service';
import {ToasterComponent} from './toaster/toaster.component';
import {StrSpinnerComponent} from './str-spinner/str-spinner.component';
import {NoCacheInterceptor} from './no-cache.interceptor';
import {environment} from '../environments/environment';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(environment.msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.webApi.uri,
    environment.webApi.scopes
  );
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.webApi.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

@NgModule({
  declarations: [
    AppComponent,
    CreateTimecardComponent,
    DetailedReportingComponent,
    ErrorComponent,
    GroupadminComponent,
    GroupMonthReportComponent,
    GroupPeriodReportComponent,
    GroupProjectReportComponent,
    GroupWeekReportComponent,
    HomeComponent,
    RecentTimecardsComponent,
    ReportsComponent,
    SysadminBatchCheckComponent,
    SysadminComponent,
    SysadminSetAdminComponent,
    SysadminSetInfomsgComponent,
    SystemUnavailComponent,
    AccessDeniedComponent,
    ToasterComponent,
    StrSpinnerComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    GroupGuardService,
    PendingChangesGuard,
    AuthGuard,
    EmployeeService,
    SystemUnavailService,
    GroupAdminService,
    InternalMsgService,
    InfoMessageService,
    ProjectService,
    ReportingService,
    SysAdminGuard,
    SysadminService,
    TaskService,
    TemplateService,
    TimeCardService,
    WeekService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
}
